import { c as _c } from "react/compiler-runtime";
import { arrayMove } from '@dnd-kit/sortable';
import { PricePlanId, PricePlanType } from '@systemeio/types';
import { CurrencyEnum, PaymentMethodEnum, toast } from '@systemeio/ui';
import { BadRequest } from '@systemeio/util';
import AddPricePlanForm from 'modules/digital-product/price-plans/components/add-price-plan-form';
import CreatePricePlanForm from 'modules/digital-product/price-plans/components/create-price-plan-form';
import EditPricePlanForm from 'modules/digital-product/price-plans/components/edit-price-plan-form';
import PricePlanBase from 'modules/digital-product/price-plans/components/price-plan-base';
import PricePlanItem from 'modules/digital-product/price-plans/components/price-plan-item';
import RemovePricePlanConfirmation from 'modules/digital-product/price-plans/components/remove-price-plan-confirmation';
import { PricePlanRemoveTypeEnum } from 'modules/digital-product/price-plans/enums/price-plan-remove-type-enum';
import { usePricePlansApi } from 'modules/digital-product/price-plans/hooks/use-price-plans-api';
import { useUserPricePlans } from 'modules/digital-product/price-plans/hooks/use-user-price-plans';
import { PricePlanCreateErrorsInterface, PricePlanCreateInterface, PricePlanEditInterface } from 'modules/digital-product/price-plans/types/price-plan-interface';
import { DigitalProductErrorInterface } from 'modules/digital-product/types/digital-product-interface';
import { AddItemIcon } from 'modules/funnels/funnel/components/offer-settings/add-item-icon';
import React, { useRef, useState } from 'react';
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description';
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch';
import { SectionMessage } from 'shared/components/section-message';
import Sortable from 'shared/components/sortable';
import { useLocoTranslation } from 'shared/hooks/use-loco-translation';
import { fetchWithErrors } from 'shared/utils/fetch-with-errors';
const defaultCreateErrors: PricePlanCreateErrorsInterface = {
  name: '',
  innerName: '',
  statementDescriptor: '',
  type: '',
  amount: '',
  statementDescriptorKatakana: '',
  taxBehavior: '',
  recurringOptions: {
    interval: '',
    intervalCount: '',
    trialPeriod: '',
    trialInterval: '',
    limitOfPayments: ''
  }
};
const defaultEditErrors = {
  name: '',
  innerName: '',
  statementDescriptor: ''
};
type Props = {
  currency: CurrencyEnum;
  pricePlans: PricePlanType[];
  errors: DigitalProductErrorInterface['pricePlans'];
  maxPricePlans: number;
  paymentMethods: PaymentMethodEnum[];
  mutatePricePlans?: (pricePlans: PricePlanType[]) => Promise<void>;
  onChanePricePlans: (pricePlans: PricePlanType[]) => void;
};
function PricePlans(t0) {
  const $ = _c(88);
  const {
    pricePlans,
    onChanePricePlans,
    maxPricePlans,
    mutatePricePlans,
    errors,
    paymentMethods,
    currency
  } = t0;
  const [isAddFormOpen, setIsAddFormOpen] = useState(false);
  const [isCreateFormOpen, setIsCreateFormOpen] = useState(false);
  const [pricePlanIdToDelete, setPricePlanIdToDelete] = useState(null);
  const [pricePlanIdToEdit, setPricePlanIdToEdit] = useState(null);
  const [pricePlanIdToReplace, setPricePlanIdToReplace] = useState(null);
  const [createErrors, setCreateErrors] = useState(defaultCreateErrors);
  const [editPricePlanErrors, setEditPricePlanErrors] = useState(defaultEditErrors);
  const [isCreateFormForReplaceOpen, setIsCreateFormForReplaceOpen] = useState(false);
  const {
    mutateUserPricePlans
  } = useUserPricePlans(currency);
  const {
    t
  } = useLocoTranslation();
  const {
    createPricePlan,
    updatePricePlan,
    deletePricePlan
  } = usePricePlansApi();
  const createdPricePlanId = useRef(undefined);
  const hasPaymentMethods = paymentMethods.length > 0;
  const allowMorePricePlans = hasPaymentMethods && pricePlans.length < maxPricePlans;
  let T0;
  let T1;
  let handleRemovePricePlan;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  if ($[0] !== allowMorePricePlans || $[1] !== createErrors || $[2] !== createPricePlan || $[3] !== currency || $[4] !== deletePricePlan || $[5] !== editPricePlanErrors || $[6] !== hasPaymentMethods || $[7] !== isAddFormOpen || $[8] !== isCreateFormForReplaceOpen || $[9] !== isCreateFormOpen || $[10] !== mutatePricePlans || $[11] !== mutateUserPricePlans || $[12] !== onChanePricePlans || $[13] !== pricePlanIdToDelete || $[14] !== pricePlanIdToEdit || $[15] !== pricePlanIdToReplace || $[16] !== pricePlans || $[17] !== t || $[18] !== updatePricePlan) {
    const handleCreatePricePlan = async (createPricePlanData, oldPricePlanId) => {
      ;
      try {
        setCreateErrors(defaultCreateErrors);
        const responseData = await createPricePlan({
          ...createPricePlanData,
          currency
        });
        await mutateUserPricePlans(data => data && [...data, responseData], false);
        await handleAddPricePlan(responseData, oldPricePlanId);
      } catch (t9) {
        const e = t9;
        if (e instanceof BadRequest) {
          if (e.errors.fields) {
            const errors_0 = e.errors.fields as unknown as PricePlanCreateErrorsInterface;
            setCreateErrors(prev => ({
              ...prev,
              ...errors_0
            }));
          }
        }
        throw e;
      }
    };
    const handleAddPricePlan = async (newPricePlan, oldPricePlanId_0) => {
      try {
        if (oldPricePlanId_0) {
          const newPricePlans = pricePlans.map(pricePlan => pricePlan.id === oldPricePlanId_0 ? newPricePlan : pricePlan);
          onChanePricePlans(newPricePlans);
        } else {
          const newPricePlans_0 = [...pricePlans, newPricePlan];
          onChanePricePlans(newPricePlans_0);
        }
        closeAddForm();
      } catch {
        toast.error(t("global.error"));
      }
    };
    let t10;
    if ($[30] !== deletePricePlan || $[31] !== mutatePricePlans || $[32] !== mutateUserPricePlans || $[33] !== onChanePricePlans || $[34] !== pricePlanIdToDelete || $[35] !== pricePlans || $[36] !== t) {
      t10 = async removeType => {
        if (!pricePlanIdToDelete) {
          return;
        }
        const updatedPricePlans = pricePlans.filter(pricePlan_0 => pricePlan_0.id !== pricePlanIdToDelete);
        try {
          onChanePricePlans(updatedPricePlans);
          if (removeType === PricePlanRemoveTypeEnum.from_account) {
            await deletePricePlan(pricePlanIdToDelete);
            await mutateUserPricePlans(data_0 => {
              if (data_0) {
                return data_0.filter(pricePlan_1 => pricePlan_1.id !== pricePlanIdToDelete);
              }
            }, false);
            if (mutatePricePlans) {
              await mutatePricePlans(updatedPricePlans);
            }
          }
        } catch {
          toast.error(t("global.error"));
        }
      };
      $[30] = deletePricePlan;
      $[31] = mutatePricePlans;
      $[32] = mutateUserPricePlans;
      $[33] = onChanePricePlans;
      $[34] = pricePlanIdToDelete;
      $[35] = pricePlans;
      $[36] = t;
      $[37] = t10;
    } else {
      t10 = $[37];
    }
    handleRemovePricePlan = t10;
    let t11;
    if ($[38] !== mutatePricePlans || $[39] !== mutateUserPricePlans || $[40] !== onChanePricePlans || $[41] !== pricePlanIdToEdit || $[42] !== pricePlans || $[43] !== t || $[44] !== updatePricePlan) {
      t11 = async updatedPricePlanData => {
        if (!pricePlanIdToEdit) {
          return;
        }
        await fetchWithErrors(async () => {
          const responseData_0 = await updatePricePlan(pricePlanIdToEdit, updatedPricePlanData);
          await mutateUserPricePlans(data_1 => {
            if (data_1) {
              return data_1.map(pricePlan_2 => {
                if (pricePlan_2.id === responseData_0.id) {
                  return {
                    ...pricePlan_2
                  };
                }
                return pricePlan_2;
              });
            }
          }, false);
          const updatedPricePlans_0 = pricePlans.map(pricePlan_3 => {
            if (pricePlan_3.id === pricePlanIdToEdit) {
              return responseData_0;
            }
            return pricePlan_3;
          });
          onChanePricePlans(updatedPricePlans_0);
          if (mutatePricePlans) {
            await mutatePricePlans(updatedPricePlans_0);
          }
          toast.success(t("global.changes_saved"));
        }, setEditPricePlanErrors);
      };
      $[38] = mutatePricePlans;
      $[39] = mutateUserPricePlans;
      $[40] = onChanePricePlans;
      $[41] = pricePlanIdToEdit;
      $[42] = pricePlans;
      $[43] = t;
      $[44] = updatePricePlan;
      $[45] = t11;
    } else {
      t11 = $[45];
    }
    const handleEditPricePlan = t11;
    let t12;
    if ($[46] === Symbol.for("react.memo_cache_sentinel")) {
      t12 = () => {
        setIsAddFormOpen(false);
        setIsCreateFormOpen(true);
      };
      $[46] = t12;
    } else {
      t12 = $[46];
    }
    const openCreateForm = t12;
    let t13;
    if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = value => {
        setIsAddFormOpen(true);
        setIsCreateFormOpen(false);
        if (value) {
          createdPricePlanId.current = value;
        }
      };
      $[47] = t13;
    } else {
      t13 = $[47];
    }
    const openAddForm = t13;
    const closeAddForm = () => {
      setIsAddFormOpen(false);
    };
    let t14;
    if ($[48] === Symbol.for("react.memo_cache_sentinel")) {
      t14 = () => {
        setIsCreateFormOpen(false);
      };
      $[48] = t14;
    } else {
      t14 = $[48];
    }
    const closeCreateForm = t14;
    const getPricePlanJSX = pricePlanData => {
      if (pricePlanData.id === pricePlanIdToEdit) {
        return <EditPricePlanForm currency={currency} key={pricePlanData.id} onEditPricePlan={handleEditPricePlan} closeForm={() => setPricePlanIdToEdit(null)} editPricePlanErrors={editPricePlanErrors} pricePlanId={pricePlanIdToEdit} />;
      }
      if (pricePlanData.id === pricePlanIdToReplace) {
        if (isCreateFormForReplaceOpen) {
          return <CreatePricePlanForm key={pricePlanData.id} onCreatePricePlan={data_2 => handleCreatePricePlan(data_2, pricePlanIdToReplace)} createErrors={createErrors} currency={currency} closeForm={() => {
            setPricePlanIdToReplace(null);
            setIsCreateFormForReplaceOpen(false);
          }} openAddForm={() => setIsCreateFormForReplaceOpen(false)} />;
        } else {
          return <AddPricePlanForm productPricePlans={pricePlans} key={pricePlanData.id} currency={currency} onAddPricePlan={pricePlan_4 => handleAddPricePlan(pricePlan_4, pricePlanIdToReplace)} closeForm={() => setPricePlanIdToReplace(null)} openCreateForm={() => setIsCreateFormForReplaceOpen(true)} />;
        }
      }
      return <PricePlanItem key={pricePlanData.id} pricePlan={pricePlanData} onChange={() => {
        setPricePlanIdToReplace(pricePlanData.id);
      }} onEdit={() => {
        setPricePlanIdToEdit(pricePlanData.id);
      }} onDelete={() => setPricePlanIdToDelete(pricePlanData.id)} />;
    };
    let t15;
    if ($[49] !== onChanePricePlans || $[50] !== pricePlans) {
      t15 = async (activeIndex, overIndex) => {
        const reorderedPricePlans = arrayMove(pricePlans, activeIndex, overIndex);
        onChanePricePlans(reorderedPricePlans);
      };
      $[49] = onChanePricePlans;
      $[50] = pricePlans;
      $[51] = t15;
    } else {
      t15 = $[51];
    }
    const onMove = t15;
    T1 = PricePlanBase;
    if ($[52] !== allowMorePricePlans || $[53] !== t) {
      t8 = allowMorePricePlans && <AddItemIcon onClick={() => setIsAddFormOpen(true)} caption={t("dashboard.funnel.configuration.offer.add_price_plan")} />;
      $[52] = allowMorePricePlans;
      $[53] = t;
      $[54] = t8;
    } else {
      t8 = $[54];
    }
    if ($[55] !== hasPaymentMethods || $[56] !== t) {
      t5 = !hasPaymentMethods && <SectionMessage className="mt-2">{t("dashboard.funnel.configuration.common.price_plan.payment_gateways_requirement-2")}<LinkWithoutPrefetch className="primary-link font-inter text-start text-sm font-normal" href="/profile/payment-gateways">{t("dashboard.funnel.configuration.common.price_plan.payment_gateways_requirement_link")}</LinkWithoutPrefetch></SectionMessage>;
      $[55] = hasPaymentMethods;
      $[56] = t;
      $[57] = t5;
    } else {
      t5 = $[57];
    }
    t6 = isAddFormOpen && <AddPricePlanForm productPricePlans={pricePlans} onAddPricePlan={handleAddPricePlan} currency={currency} closeForm={closeAddForm} openCreateForm={openCreateForm} />;
    t7 = isCreateFormOpen && <CreatePricePlanForm onCreatePricePlan={handleCreatePricePlan} createErrors={createErrors} currency={currency} closeForm={closeCreateForm} openAddForm={openAddForm} />;
    T0 = Sortable;
    t1 = getPricePlanJSX;
    t2 = pricePlans;
    if ($[58] !== onMove) {
      t3 = (_, activeIndex_0, overIndex_0) => onMove(activeIndex_0, overIndex_0);
      $[58] = onMove;
      $[59] = t3;
    } else {
      t3 = $[59];
    }
    t4 = pricePlans.map(getPricePlanJSX);
    $[0] = allowMorePricePlans;
    $[1] = createErrors;
    $[2] = createPricePlan;
    $[3] = currency;
    $[4] = deletePricePlan;
    $[5] = editPricePlanErrors;
    $[6] = hasPaymentMethods;
    $[7] = isAddFormOpen;
    $[8] = isCreateFormForReplaceOpen;
    $[9] = isCreateFormOpen;
    $[10] = mutatePricePlans;
    $[11] = mutateUserPricePlans;
    $[12] = onChanePricePlans;
    $[13] = pricePlanIdToDelete;
    $[14] = pricePlanIdToEdit;
    $[15] = pricePlanIdToReplace;
    $[16] = pricePlans;
    $[17] = t;
    $[18] = updatePricePlan;
    $[19] = T0;
    $[20] = T1;
    $[21] = handleRemovePricePlan;
    $[22] = t1;
    $[23] = t2;
    $[24] = t3;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    T0 = $[19];
    T1 = $[20];
    handleRemovePricePlan = $[21];
    t1 = $[22];
    t2 = $[23];
    t3 = $[24];
    t4 = $[25];
    t5 = $[26];
    t6 = $[27];
    t7 = $[28];
    t8 = $[29];
  }
  let t9;
  if ($[60] !== T0 || $[61] !== t1 || $[62] !== t2 || $[63] !== t3 || $[64] !== t4) {
    t9 = <T0 draggable={t1} items={t2} onMove={t3}>{t4}</T0>;
    $[60] = T0;
    $[61] = t1;
    $[62] = t2;
    $[63] = t3;
    $[64] = t4;
    $[65] = t9;
  } else {
    t9 = $[65];
  }
  let t10;
  if ($[66] !== errors) {
    t10 = errors.join(", ");
    $[66] = errors;
    $[67] = t10;
  } else {
    t10 = $[67];
  }
  let t11;
  if ($[68] !== t10) {
    t11 = <FieldErrorAndDescription error={t10} errorClassName="text-center" />;
    $[68] = t10;
    $[69] = t11;
  } else {
    t11 = $[69];
  }
  let t12;
  if ($[70] !== t11 || $[71] !== t5 || $[72] !== t6 || $[73] !== t7 || $[74] !== t9) {
    t12 = <>{t5}{t6}{t7}{t9}{t11}</>;
    $[70] = t11;
    $[71] = t5;
    $[72] = t6;
    $[73] = t7;
    $[74] = t9;
    $[75] = t12;
  } else {
    t12 = $[75];
  }
  let t13;
  if ($[76] !== T1 || $[77] !== t12 || $[78] !== t8) {
    t13 = <T1 additionalChild={t8}>{t12}</T1>;
    $[76] = T1;
    $[77] = t12;
    $[78] = t8;
    $[79] = t13;
  } else {
    t13 = $[79];
  }
  let t14;
  if ($[80] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = () => setPricePlanIdToDelete(null);
    $[80] = t14;
  } else {
    t14 = $[80];
  }
  let t15;
  if ($[81] !== currency || $[82] !== handleRemovePricePlan || $[83] !== pricePlanIdToDelete) {
    t15 = <RemovePricePlanConfirmation currency={currency} pricePlanId={pricePlanIdToDelete} onRemove={handleRemovePricePlan} afterLeave={t14} />;
    $[81] = currency;
    $[82] = handleRemovePricePlan;
    $[83] = pricePlanIdToDelete;
    $[84] = t15;
  } else {
    t15 = $[84];
  }
  let t16;
  if ($[85] !== t13 || $[86] !== t15) {
    t16 = <>{t13}{t15}</>;
    $[85] = t13;
    $[86] = t15;
    $[87] = t16;
  } else {
    t16 = $[87];
  }
  return t16;
}
export default PricePlans;